import * as React from 'react';
import { Info } from 'lucide-react';
import { useForm, useStore } from '@tanstack/react-form';

import { CardRadioInput } from '@/shared/components/ui/inputs/card-radio-input';
import { TextInput } from '@/shared/components/ui/inputs/text-input';
import { DialogFormFooter } from '@/shared/components/ui/form/dialog-form-footer';
import { fieldToInputProps, toFormProps } from '@/utils/shared/form';
import { FormProps } from '@/types/form/form-props';
import { PayoutMethod } from '@/types/api/payout-account';

import { CreatePayoutAccountInput, CreatePayoutAccountInputSchema } from '../api/create-payout-account';
import { SelectBankAccount } from './select-bank-account';
import { PAYOUT_METHOD_OPTIONS } from '../utils/payout-method-options';

export function CreatePayoutAccountForm({
  onSubmit,
  defaultValues,
  Footer = DialogFormFooter,
}: FormProps<CreatePayoutAccountInput>) {
  const form = useForm({
    defaultValues: {
      method: PayoutMethod.Swish,
      selectionId: '',
      swishNumber: '',
      ...defaultValues,
    } as any,
    validators: { onSubmit: CreatePayoutAccountInputSchema },
    onSubmit: ({ value }) => onSubmit(value),
  });

  const method = useStore(form.store, (state) => state.values.method);
  // Only after the leader picks a method, so the form doesn't grab focus (and open the keyboard) on load
  const [methodChosen, setMethodChosen] = React.useState(false);
  const selectionId = useStore(form.store, (state) => state.values.selectionId);

  React.useEffect(() => {
    //prettier-ignore
    switch (method) {
      case PayoutMethod.BankTransfer: form.setFieldMeta('swishNumber', (state) => ({ ...state, errorMap: {} })); break;
      case PayoutMethod.Swish:        form.setFieldMeta('selectionId', (state) => ({ ...state, errorMap: {} })); break;
    }
  }, [method, form]);

  // A bank account is chosen at the bank, so there is nothing to save until the leader is back with one
  const awaitingBankAccount = method === PayoutMethod.BankTransfer && !selectionId;

  // The fields of the selected method, shown inside its card
  const methodFields: Record<PayoutMethod, React.ReactNode> = {
    [PayoutMethod.Swish]: (
      <form.Field name="swishNumber">
        {(field) => (
          <TextInput
            {...fieldToInputProps(field, 'Mobilnummer för Swish')}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus={methodChosen}
          />
        )}
      </form.Field>
    ),
    [PayoutMethod.BankTransfer]: (
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Du väljer konto hos din bank och loggar in med BankID. Sen kommer du tillbaka hit och sparar.
        </p>
        <form.Field name="selectionId">{(field) => <SelectBankAccount {...fieldToInputProps(field, '')} />}</form.Field>
      </div>
    ),
  };

  return (
    <form {...toFormProps(form)} className="space-y-6">
      {/* Rocker only pays out to the person who verified with BankID */}
      <div className="flex gap-3 rounded-md border px-4 py-3">
        <Info className="shrink-0 mt-0.5 text-violet-500" size={16} aria-hidden="true" />
        <p className="text-sm">
          Utbetalningen måste gå till ett Swish-nummer eller bankkonto i ditt namn, samma person som verifierade sig
          med BankID.
        </p>
      </div>

      <form.Field name="method">
        {(field) => (
          <CardRadioInput
            {...fieldToInputProps(field, 'Utbetalningsmetod')}
            onChange={(value) => {
              field.handleChange(value);
              setMethodChosen(true);
            }}
            options={PAYOUT_METHOD_OPTIONS.map((option) => ({ ...option, content: methodFields[option.value] }))}
          />
        )}
      </form.Field>

      <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
        {([canSubmit, isSubmitting]) => (
          <Footer canSubmit={canSubmit && !awaitingBankAccount} isSubmitting={isSubmitting} submitText="Lägg till konto" />
        )}
      </form.Subscribe>
    </form>
  );
}
